/**
 * Judge-line bodies (W2-01a preview-gl; rendering.md R§3.4-R§3.7, R§8 step 4, ARCHITECTURE.md §7.3 step 3).
 *
 * `list.lines` is already in Phira's (zOrder, index) order with UI movers left out; every body is drawn in that
 * order, so a texture, text or paint line between two normal lines keeps its place (consecutive normal lines share
 * one draw call: same program, white texture and transform).
 *  - normal: a plain quad x in [-halfLen, halfLen], y in [-thickness/2, thickness/2] under `m` (no caps);
 *  - texture: the unit quad [-0.5, 0.5]^2 under `m` (pixel size and scale already in `m`), image top row towards
 *    local +y, colour = tint with the replaced alpha; bilinear, no mipmaps (Phira reloads textures without them);
 *  - gif: the same quad with the frame at `gifMs` chosen from Phira's raw delays; the first frame (a plain image
 *    decode) is shown while the GIF is still being decoded;
 *  - text: the rasterised block (textCache.ts) in the block's local space (y down, em = 0.08 units, anchored at the
 *    centre of [first ascent line, last baseline] x [0, widest advance]);
 *  - paint: the line's persistent buffer composited right here (paintPass.ts).
 */

import type { Engine, LineDraw } from '../../core/engine/types';
import type { Mat2D } from '../../core/util/types';
import { beginQuads, rgbaBytes, type PassContext } from './passContext';
import type { PaintBuffers } from './paintPass';
import type { TextRaster } from './textCache';

/** Text block em (PxScale) in world units before the line scale: 0.04 W px = 0.08 units (R§3.6). */
export const TEXT_EM = 0.08;

/** Device px of one PxScale for a text drawn under `m` (local units -> world -> px). */
export function textPxScale(m: Mat2D, em: number, pxPerUnitX: number, pxPerUnitY: number): number {
  const sx = Math.hypot(m[0] * pxPerUnitX, m[1] * pxPerUnitY);
  const sy = Math.hypot(m[2] * pxPerUnitX, m[3] * pxPerUnitY);
  const s = Math.max(sx, sy) * em;
  return Number.isFinite(s) ? s : 0;
}

/**
 * Local rectangle of a raster whose layout box (blockW x blockH PxScale units) has its top-left corner at
 * (left, top) local units, `em` local units per PxScale unit: [x0, y0, x1, y1].
 */
export function rasterRect(
  r: TextRaster,
  left: number,
  top: number,
  em: number,
): readonly [number, number, number, number] {
  const k = em / r.pxScale;
  const x0 = left - r.padX * k;
  const y0 = top - r.padY * k;
  return [x0, y0, x0 + r.width * k, y0 + r.height * k];
}

function drawNormal(ctx: PassContext, d: Extract<LineDraw, { kind: 'normal' }>): void {
  const [r, g, b, a] = rgbaBytes(d.color);
  if (a === 0) return;
  beginQuads(ctx, ctx.worldToClip, null);
  const h = d.thickness / 2;
  ctx.batch.rect(d.m, -d.halfLen, -h, d.halfLen, h, 0, 0, 1, 1, r, g, b, a);
}

function drawImageQuad(
  ctx: PassContext,
  m: Mat2D,
  image: ImageBitmap,
  color: readonly [number, number, number, number],
): void {
  const tex = ctx.textures.get(image);
  if (tex === null) return;
  beginQuads(ctx, ctx.worldToClip, tex);
  // (x0, y0) is the bottom-left of the unit quad: the image's bottom row (v = 1).
  ctx.batch.rect(m, -0.5, -0.5, 0.5, 0.5, 0, 1, 1, 0, color[0], color[1], color[2], color[3]);
}

function drawTexture(ctx: PassContext, d: Extract<LineDraw, { kind: 'texture' }>): void {
  const color = rgbaBytes(d.color);
  if (color[3] === 0 || ctx.provider === null) return;
  const img = ctx.provider.image(d.asset);
  if (img !== null) drawImageQuad(ctx, d.m, img, color);
}

function drawGif(ctx: PassContext, d: Extract<LineDraw, { kind: 'gif' }>): void {
  const color = rgbaBytes(d.color);
  if (ctx.provider === null) return;
  const gif = ctx.provider.gif(d.asset);
  if (gif !== null) {
    const frame = gif.frames[gif.frameAt(d.gifMs)] ?? gif.frames[0];
    if (frame !== undefined) drawImageQuad(ctx, d.m, frame, color);
    return;
  }
  const first = ctx.provider.image(d.asset);
  if (first !== null) drawImageQuad(ctx, d.m, first, color);
}

function drawText(ctx: PassContext, d: Extract<LineDraw, { kind: 'text' }>): void {
  const color = rgbaBytes(d.color);
  if (color[3] === 0) return;
  const px = textPxScale(d.m, TEXT_EM, ctx.pxPerUnitX, ctx.pxPerUnitY);
  if (!(px >= 0.5)) return;
  const r = ctx.text.get(d.text, 'ui', px);
  if (r === null) return;
  const tex = ctx.textures.get(r.canvas);
  if (tex === null) return;
  const [x0, y0, x1, y1] = rasterRect(r, (-r.blockW / 2) * TEXT_EM, (-r.blockH / 2) * TEXT_EM, TEXT_EM);
  beginQuads(ctx, ctx.worldToClip, tex);
  ctx.batch.rect(d.m, x0, y0, x1, y1, 0, 0, 1, 1, color[0], color[1], color[2], color[3]);
}

/** Draws every line body of the frame in order (paint buffers composited in place). */
export function drawLines(ctx: PassContext, paint: PaintBuffers, engine: Engine): void {
  const live = new Set<LineDraw['line']>();
  for (const d of ctx.list.lines) {
    switch (d.kind) {
      case 'normal':
        drawNormal(ctx, d);
        break;
      case 'texture':
        drawTexture(ctx, d);
        break;
      case 'gif':
        drawGif(ctx, d);
        break;
      case 'text':
        drawText(ctx, d);
        break;
      case 'paint':
        live.add(d.line);
        ctx.batch.flush();
        paint.composite(ctx, d.line, engine);
        break;
    }
  }
  paint.retain(live);
}
